/**
 * Wiggly Stuff — Wishlist
 * localStorage-backed wishlist. No backend required.
 * Storage key: wiggly_wishlist → JSON array of product handles
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'wiggly_wishlist';

  function track(type, payload) {
    try {
      window.dispatchEvent(new CustomEvent('ws:track', { detail: { type: type, payload: payload } }));
    } catch {
      // Optional tracking.
    }
  }

  var wishlistRevision = 0;
  var productDetails = Object.create(null);
  function trackWishlistState() {
    var revision = ++wishlistRevision;
    var handles = WishlistStore.get().filter(function (handle) {
      return typeof handle === 'string' && /^[a-z0-9][a-z0-9-]*$/.test(handle);
    });
    track('wishlist_state', { items: handles.map(function (handle) { return { handle: handle }; }) });
    // shortcut: enrich the first 50 saves, raise the cap if larger wishlists become common.
    Promise.all(handles.map(function (handle, index) {
      if (index >= 50) return { handle: handle };
      if (!productDetails[handle]) {
        var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
        productDetails[handle] = fetch(root + 'products/' + encodeURIComponent(handle) + '.js')
          .then(function (response) { if (!response.ok) throw new Error('Product unavailable'); return response.json(); })
          .then(function (product) { return { handle: handle, product_id: product.id, title: product.title, image: product.featured_image }; })
          .catch(function () { delete productDetails[handle]; return { handle: handle }; });
      }
      return productDetails[handle];
    })).then(function (items) {
      if (revision === wishlistRevision && items.length) track('wishlist_state', { items: items });
    }).catch(function () {});
  }

  // ─── Store ──────────────────────────────────────────────────────────────────

  var WishlistStore = {
    get: function () {
      try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
      } catch (e) {
        return [];
      }
    },
    save: function (list) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
      } catch (e) {
        // localStorage unavailable (private browsing, storage full, etc.)
      }
    },
    has: function (handle) {
      return this.get().indexOf(handle) !== -1;
    },
    add: function (handle) {
      var list = this.get();
      if (list.indexOf(handle) === -1) {
        list.push(handle);
        this.save(list);
        this._emit();
        return true;
      }
      return false;
    },
    remove: function (handle) {
      var current = this.get();
      if (current.indexOf(handle) === -1) return false;
      var list = current.filter(function (h) { return h !== handle; });
      this.save(list);
      this._emit();
      return true;
    },
    toggle: function (handle) {
      if (this.has(handle)) {
        if (this.remove(handle)) {
          track('wishlist_remove', { product_handle: handle, item_count: this.count() });
        }
        return false;
      } else {
        if (this.add(handle)) {
          track('wishlist_add', { product_handle: handle, item_count: this.count() });
        }
        return true;
      }
    },
    count: function () {
      return this.get().length;
    },
    _emit: function () {
      document.dispatchEvent(new CustomEvent('wishlist:changed'));
      trackWishlistState();
    }
  };

  // ─── UI ─────────────────────────────────────────────────────────────────────

  var WishlistUI = {
    init: function () {
      var self = this;

      // Bind all heart buttons already in DOM
      document.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
        self.bindBtn(btn);
      });

      // Bind hearts on cards added later (e.g. collection "Load more"), mirroring
      // the variant-selector observer in custom.js.
      var observer = new MutationObserver(function (mutations) {
        var added = false;
        mutations.forEach(function (mutation) {
          if (mutation.addedNodes.length) added = true;
        });
        if (!added) return;
        document
          .querySelectorAll('[data-wishlist-btn]:not([data-wishlist-bound])')
          .forEach(function (btn) {
            self.bindBtn(btn);
          });
      });
      observer.observe(document.body, { childList: true, subtree: true });

      // Keep nav badge in sync
      document.addEventListener('wishlist:changed', function () {
        self.syncNav();
      });

      // Initial sync
      self.syncNav();
      trackWishlistState();

      // If we're on the wishlist page, load products
      if (document.querySelector('[data-wishlist-page]')) {
        self.renderPage();
      }
    },

    bindBtn: function (btn) {
      if (btn.getAttribute('data-wishlist-bound')) return;
      var handle = btn.getAttribute('data-product-handle');
      if (!handle) return;
      btn.setAttribute('data-wishlist-bound', 'true');

      // Set initial state
      this._updateBtnState(btn, WishlistStore.has(handle));

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var added = WishlistStore.toggle(handle);
        WishlistUI._updateBtnState(btn, added);

        // If on wishlist page and removing, fade out the card
        if (!added) {
          var card = btn.closest('[data-wishlist-card]');
          if (card) {
            card.style.transition = 'opacity 0.3s';
            card.style.opacity = '0';
            setTimeout(function () {
              card.remove();
              WishlistUI._updatePageCount();
              WishlistUI._checkEmpty();
            }, 310);
          }
        }
      });
    },

    _updateBtnState: function (btn, isWishlisted) {
      if (isWishlisted) {
        btn.classList.add('is-wishlisted');
        btn.setAttribute('aria-label', 'Remove from wishlist');
        btn.setAttribute('title', 'Remove from wishlist');
      } else {
        btn.classList.remove('is-wishlisted');
        btn.setAttribute('aria-label', 'Add to wishlist');
        btn.setAttribute('title', 'Add to wishlist');
      }
    },

    syncNav: function () {
      var count = WishlistStore.count();
      document.querySelectorAll('[data-wishlist-nav-badge]').forEach(function (badge) {
        badge.textContent = count;
        if (count > 0) {
          badge.classList.remove('d-none');
        } else {
          badge.classList.add('d-none');
        }
      });
    },

    // ── Wishlist page ──────────────────────────────────────────────────────────

    renderPage: function () {
      var sharedHandles = WishlistUI._parseSharedParam();
      var isShared = sharedHandles.length > 0;
      var handles = isShared ? sharedHandles : WishlistStore.get();
      var grid = document.querySelector('[data-wishlist-grid]');
      var emptyState = document.querySelector('[data-wishlist-empty]');
      var sidebar = document.querySelector('[data-wishlist-sidebar]');
      var sharedBanner = document.querySelector('[data-wishlist-shared-banner]');
      var viewMineLink = document.querySelector('[data-wishlist-view-mine]');
      var mergeBtn = document.querySelector('[data-wishlist-merge]');
      var addAllBtn = document.querySelector('[data-wishlist-add-all]');
      var clearAllBtn = document.querySelector('[data-wishlist-clear-all]');
      var shareBtn = document.querySelector('[data-wishlist-share]');
      var emailBtn = document.querySelector('[data-wishlist-email]');

      if (!grid) return;

      if (isShared) {
        if (sharedBanner) sharedBanner.classList.remove('d-none');
        if (viewMineLink) viewMineLink.setAttribute('href', window.location.pathname);
        if (clearAllBtn) clearAllBtn.classList.add('d-none');
        if (shareBtn) shareBtn.classList.add('d-none');
        if (emailBtn) emailBtn.classList.add('d-none');
        if (mergeBtn) this._bindMerge(mergeBtn, sharedHandles);
      }

      if (handles.length === 0) {
        this._showEmpty(emptyState, sidebar);
        return;
      }

      var self = this;

      // Separate handles into pool hits and misses
      var poolHits = [];
      var poolMisses = [];

      handles.forEach(function (handle) {
        var slot = document.querySelector('[data-wishlist-slot="' + handle + '"]');
        if (slot) {
          poolHits.push({ handle: handle, slot: slot });
        } else {
          poolMisses.push(handle);
        }
      });

      // Clone pool cards immediately
      poolHits.forEach(function (item) {
        var clone = item.slot.cloneNode(true);
        clone.removeAttribute('data-wishlist-slot');
        clone.setAttribute('data-wishlist-card', '');
        clone.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
          btn.removeAttribute('data-wishlist-bound');
          self.bindBtn(btn);
        });
        if (!isShared) {
          self._appendRemoveLink(clone, item.handle);
        }
        grid.appendChild(clone);
      });

      // For pool misses, fetch from API — only remove confirmed missing products.
      var fetchPromises = poolMisses.map(function (handle) {
        return fetch('/products/' + handle + '.js')
          .then(function (r) {
            if (!r.ok) {
              if ((r.status === 404 || r.status === 410) && !isShared) {
                WishlistStore.remove(handle);
                return;
              }
              throw new Error('Unable to load wishlist item');
            }
            return r.json();
          })
          .then(function (product) {
            if (!product) return;
            var card = self._buildFallbackCard(product);
            card.querySelectorAll('[data-wishlist-btn]').forEach(function (btn) {
              self.bindBtn(btn);
            });
            if (!isShared) {
              self._appendRemoveLink(card, handle);
            }
            grid.appendChild(card);
          })
          .catch(function () {
            var card = document.createElement('div');
            card.className = 'p-4 p-desktop-5 mb-4 mb-desktop-5';
            card.setAttribute('role', 'listitem');
            card.setAttribute('data-wishlist-card', '');
            card.innerHTML = '<p>Couldn\'t load ' + self._esc(handle) + '.</p><button type="button" class="btn btn-outline-primary btn-sm">Try again</button>';
            card.querySelector('button').addEventListener('click', function () {
              window.location.reload();
            });
            grid.appendChild(card);
          });
      });

      Promise.all(fetchPromises).then(function () {
        var validCount = grid.querySelectorAll('[data-wishlist-card]').length;
        self._updatePageCount(validCount);

        if (validCount === 0) {
          self._showEmpty(emptyState, sidebar);
          return;
        }

        if (emptyState) emptyState.classList.add('d-none');
        if (sidebar) sidebar.classList.remove('d-none');

        if (addAllBtn) self._bindAddAll(addAllBtn, grid);

        if (isShared) {
          return;
        }

        if (clearAllBtn) self._bindClearAll(clearAllBtn, grid, emptyState, sidebar);
        if (shareBtn) self._bindShare(shareBtn);
        if (emailBtn) self._bindEmail(emailBtn, grid);
      });
    },

    _parseSharedParam: function () {
      try {
        var raw = new URLSearchParams(window.location.search).get('items');
        var handles = [];
        var seen = {};

        if (!raw) return [];

        raw.split(',').forEach(function (part) {
          var handle;
          if (handles.length >= 50) return;
          handle = part.replace(/^\s+|\s+$/g, '');
          if (!handle || !/^[a-z0-9][a-z0-9-]*$/.test(handle) || seen[handle]) return;
          seen[handle] = true;
          handles.push(handle);
        });

        return handles;
      } catch (e) {
        return [];
      }
    },

    _buildShareUrl: function () {
      return this._canonicalOrigin() + window.location.pathname + '?items=' + WishlistStore.get().slice(0, 50).join(',');
    },

    _canonicalOrigin: function () {
      var page = document.querySelector('[data-wishlist-page]');
      var origin = page && page.getAttribute('data-wishlist-origin');
      return (origin || window.location.origin).replace(/\/+$/, '');
    },

    _bindMerge: function (btn, handles) {
      btn.addEventListener('click', function () {
        handles.forEach(function (handle) {
          WishlistStore.add(handle);
        });
        window.location.href = window.location.pathname;
      });
    },

    _bindShare: function (btn) {
      // navigator.share and clipboard.writeText need a secure context (HTTPS).
      // On non-HTTPS previews the window.prompt fallback is expected behavior.
      btn.addEventListener('click', function () {
        var url = WishlistUI._buildShareUrl();
        // innerHTML, not textContent — the button label includes an icon element
        var originalHtml = btn.innerHTML;

        if (navigator.share) {
          try {
            var shareResult = navigator.share({
              title: 'My Wiggly Stuff Wishlist',
              url: url
            });
            if (shareResult && typeof shareResult.catch === 'function') {
              shareResult.then(function () {
                track('wishlist_share', { method: 'native', item_count: WishlistStore.get().slice(0, 50).length });
              }).catch(function () {});
            }
          } catch (e) {}
          return;
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url)
            .then(function () {
              track('wishlist_share', { method: 'copy', item_count: WishlistStore.get().slice(0, 50).length });
              btn.textContent = 'Link Copied!';
              setTimeout(function () {
                btn.innerHTML = originalHtml;
              }, 2000);
            })
            .catch(function () {
              window.prompt('Copy your wishlist link:', url);
              track('wishlist_share', { method: 'prompt', item_count: WishlistStore.get().slice(0, 50).length });
            });
          return;
        }

        window.prompt('Copy your wishlist link:', url);
        track('wishlist_share', { method: 'prompt', item_count: WishlistStore.get().slice(0, 50).length });
      });
    },

    _bindEmail: function (btn, grid) {
      btn.addEventListener('click', function () {
        var items = [];
        var cards = grid.querySelectorAll('[data-wishlist-card]');
        var totalItems = 0;
        var subject = 'My Wiggly Stuff Wishlist 🎁';
        var body;

        cards.forEach(function (card) {
          var titleLink;
          var href;
          var title;

          titleLink = card.querySelector('.product-card-alt__title a');
          if (!titleLink) return;
          totalItems += 1;
          // The full URL carries every item; keep the email preview short.
          if (items.length >= 5) return;

          href = titleLink.getAttribute('href') || '';
          title = titleLink.textContent.replace(/^\s+|\s+$/g, '');
          items.push('• ' + title + ' — ' + WishlistUI._canonicalUrl(href));
        });

        body = 'Hi! Here\'s my Wiggly Stuff wishlist.\n\nView the full list:\n' + WishlistUI._buildShareUrl();
        if (items.length) {
          body += '\n\nOn the list:\n' + items.join('\n');
          if (totalItems > items.length) body += '\n+ ' + (totalItems - items.length) + ' more on the full list.';
        }
        body += '\n\nThanks!';
        track('wishlist_share', {
          method: 'email',
          item_count: WishlistStore.get().slice(0, 50).length
        });
        window.location.href = 'mailto:?subject=' + encodeURIComponent(subject) + '&body=' + encodeURIComponent(body);
      });
    },

    _canonicalUrl: function (href) {
      try {
        var url = new URL(href, this._canonicalOrigin());
        return this._canonicalOrigin() + url.pathname + url.search + url.hash;
      } catch {
        return this._canonicalOrigin() + href;
      }
    },

    // Bind the ATC submit button inside a cloned pool card
    _bindCardAtc: function (form, card) {
      // product-card-form uses a <product-form> custom element which handles submission natively.
    },

    // Fallback card for products not in the Liquid pool — matches product-card-alt structure
    _buildFallbackCard: function (product) {
      var variant = null;
      for (var i = 0; i < product.variants.length; i++) {
        if (product.variants[i].available) { variant = product.variants[i]; break; }
      }
      if (!variant) variant = product.variants[0];

      var rawImage = product.featured_image || (product.images && product.images[0]) || '';
      var image = this._shopifyImageUrl(rawImage, 600);
      // ponytail: the product JSON has no metafields, so builder products are listed by handle. Add new builders here.
      var isMulti = product.variants.length > 1 || ['build-your-own-christmas-clicker', 'build-your-own-keycap-clicker'].indexOf(product.handle) !== -1;
      var requiresChoice = product.gift_card || isMulti;
      var price = this._money(variant.price);
      var url = '/products/' + this._esc(product.handle);

      var btnStyle = 'background-color:var(--yellow);border:var(--border-width) solid var(--black)!important;border-radius:var(--border-radius)!important;color:var(--black);font-family:var(--font-bungee);text-transform:uppercase;letter-spacing:var(--bungee-letter-spacing);padding:0.5rem 0.75rem;transition:var(--transition);box-shadow:var(--shadow);';
      var btnHover = 'onmouseover="this.style.transform=\'translate(-2px,-2px)\';this.style.boxShadow=\'var(--shadow-lg)\';" onmouseout="this.style.transform=\'\';this.style.boxShadow=\'var(--shadow)\';"';

      // Always include the hidden input so Add All to Cart can pick it up.
      // For multi-variant products the first available variant is used; the modal will flag these.
      var atcHtml = isMulti
        ? '<input type="hidden" class="product-card-form-variant-input" value="' + variant.id + '">'
            + '<a href="' + url + '" class="btn btn-atc btn-sm w-100 flex-grow-1" style="' + btnStyle + '" ' + btnHover + '>Choose Options</a>'
        : '<form action="/cart/add" method="post" class="flex-grow-1" style="width:auto;">'
            + '<input type="hidden" name="id" value="' + variant.id + '" class="product-card-form-variant-input">'
            + '<div class="d-flex align-items-stretch">'
            + '<button type="submit" class="btn btn-atc btn-sm w-100 flex-grow-1" style="' + btnStyle + '" ' + btnHover + '>Add to Cart</button>'
            + '</div></form>';

      var viewBtn = '<a href="' + url + '" class="btn btn-view-product-icon btn-sm d-inline-flex align-items-center justify-content-center" aria-label="View product: ' + this._esc(product.title) + '" title="View product" style="background-color:var(--white);border:var(--border-width) solid var(--black)!important;border-radius:var(--border-radius)!important;color:var(--black);line-height:1;box-shadow:var(--shadow);transition:var(--transition);" ' + btnHover + '>'
        + '<span class="visually-hidden">View product</span>'
        + '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"></path><circle cx="12" cy="12" r="3"></circle></svg>'
        + '</a>';

      var wrapper = document.createElement('div');
      wrapper.className = 'p-4 p-desktop-5 mb-4 mb-desktop-5';
      wrapper.setAttribute('role', 'list-item');
      wrapper.setAttribute('data-wishlist-card', '');
      if (requiresChoice) wrapper.setAttribute('data-wishlist-requires-choice', '');

      wrapper.innerHTML = [
        '<div class="product-card-alt product-card text-center' + (isMulti ? ' product-card-alt--multi-variant' : '') + '">',
          '<div class="product-card-link">',
            '<div class="product-card-visual position-relative">',
              '<a href="' + url + '" class="text-decoration-none">',
                '<div class="product-card-img-wrapper position-relative" style="aspect-ratio:1/1;background-color:var(--black);">',
                  image ? '<img src="' + image + '" alt="' + this._esc(product.title) + '" class="product-card-image w-100 h-100 object-fit-cover" width="600" height="600" loading="lazy">' : '',
                '</div>',
              '</a>',
            '</div>',
            '<div class="product-card-alt__body">',
              '<h3 class="product-card-alt__title"><a href="' + url + '" class="text-decoration-none">' + this._esc(product.title) + '</a></h3>',
              '<div class="product-card-alt__footer">',
                '<div class="product-card-alt__price">',
                  '<p class="product-card-price product-price mb-4"><span class="product-card-price-final">' + price + '</span></p>',
                '</div>',
                '<div class="product-card-buttons d-flex align-items-stretch gap-2 w-100">',
                  atcHtml,
                  viewBtn,
                '</div>',
              '</div>',
            '</div>',
          '</div>',
        '</div>'
      ].join('');

      return wrapper;
    },

    _appendRemoveLink: function (card, handle) {
      var link = document.createElement('button');
      link.type = 'button';
      link.className = 'wishlist-remove-link';
      link.textContent = '× Remove from wishlist';
      link.addEventListener('click', function () {
        if (!WishlistStore.remove(handle)) return;
        track('wishlist_remove', { product_handle: handle, item_count: WishlistStore.count() });
        card.style.transition = 'opacity 0.3s';
        card.style.opacity = '0';
        setTimeout(function () {
          card.remove();
          WishlistUI._updatePageCount();
          WishlistUI._checkEmpty();
        }, 310);
      });

      // Insert inside the card border, after the buttons row
      var buttonsRow = card.querySelector('.product-card-buttons');
      if (buttonsRow && buttonsRow.parentNode) {
        buttonsRow.parentNode.insertBefore(link, buttonsRow.nextSibling);
      } else {
        // fallback: append to the inner .product-card element
        var inner = card.querySelector('.product-card');
        if (inner) inner.appendChild(link);
        else card.appendChild(link);
      }
    },

    _shopifyImageUrl: function (url, size) {
      if (!url) return '';
      var sep = url.indexOf('?') !== -1 ? '&' : '?';
      return url + sep + 'width=' + size + '&height=' + size + '&crop=center';
    },

    _money: function (cents) {
      return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
    },

    _esc: function (str) {
      return String(str)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
    },

    _bindAddAll: function (btn, grid) {
      var autoModal = document.getElementById('wishlist-auto-variant-modal');
      if (autoModal && !btn.dataset.wishlistVariantFocusBound) {
        btn.dataset.wishlistVariantFocusBound = 'true';
        autoModal.addEventListener('hidden.bs.modal', function () {
          btn.focus();
        });
      }

      btn.addEventListener('click', function () {
        var items = [];
        var multiVariantProducts = [];
        var showVariantPrompt = function (onlyChoices) {
          if (!multiVariantProducts.length || !window.bootstrap) return false;
          var autoModal = document.getElementById('wishlist-auto-variant-modal');
          if (!autoModal) return false;
          var listEl = document.getElementById('wishlist-auto-variant-list');
          if (listEl) {
            listEl.textContent = '';
            multiVariantProducts.forEach(function (p) {
              var item = document.createElement('li');
              var link = document.createElement('a');
              link.href = p.url;
              link.style.color = 'var(--black)';
              link.style.fontWeight = '600';
              link.textContent = p.title;
              item.appendChild(link);
              listEl.appendChild(item);
            });
          }
          var title = autoModal.querySelector('[data-wishlist-variant-title]');
          var message = autoModal.querySelector('[data-wishlist-variant-message]');
          var action = autoModal.querySelector('[data-wishlist-variant-action]');
          var choiceLabel = action?.dataset.choiceLabel || 'Choose an option';
          if (title) title.textContent = onlyChoices ? choiceLabel : 'Check Your Cart';
          if (message) message.textContent = onlyChoices
            ? 'Choose an option for each item below:'
            : 'Everything else was added. Choose an option for these items:';
          if (action) {
            action.textContent = onlyChoices ? choiceLabel : 'View Cart';
            action.href = onlyChoices ? multiVariantProducts[0].url : action.dataset.cartUrl;
          }
          window.bootstrap.Modal.getOrCreateInstance(autoModal).show();
          return true;
        };

        grid.querySelectorAll('[data-wishlist-card]').forEach(function (card) {
          var inner = card.hasAttribute('data-wishlist-requires-choice');
          var input = card.querySelector('input.product-card-form-variant-input');
          if (!inner && input && input.value) {
            items.push({ id: parseInt(input.value, 10), quantity: 1 });
          }
          if (inner) {
            var titleEl = card.querySelector('.product-card-alt__title a');
            if (titleEl) {
              multiVariantProducts.push({
                title: titleEl.textContent.trim(),
                url: titleEl.getAttribute('href')
              });
            }
          }
        });

        if (!items.length) {
          btn.disabled = false;
          btn.textContent = 'Add All to Cart';
          showVariantPrompt(true);
          return;
        }

        btn.disabled = true;
        btn.textContent = 'Adding…';

        var CART_SECTIONS = ['cart-count-badge', 'offcanvas-cart'];
        var cartEl = document.querySelector('cart-container');
        var mutation = cartEl && typeof cartEl.beginMutation === 'function' ? cartEl.beginMutation() : null;

        Promise.resolve().then(async function () {
          var surprise = items.find(function (item) { return String(item.id) === '67705281806653'; });
          if (surprise) await window.theme.checkSurpriseCartAddition(surprise.id);
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: items, sections: CART_SECTIONS })
          });
        })
          .then(function (r) {
            return r.json().then(function (data) {
              if (!r.ok) throw new Error(data.description || 'Failed to add items to cart');
              return data;
            });
          })
          .then(async function (data) {
            cartEl = document.querySelector('cart-container');
            if (cartEl) {
              if (!data.sections || typeof cartEl.reloadCartElements !== 'function') {
                btn.disabled = true;
                btn.textContent = 'Cart updated — refresh page';
                return;
              }
              var replacement = mutation != null && typeof cartEl.applyMutation === 'function'
                ? await cartEl.applyMutation(mutation, data.sections)
                : cartEl.reloadCartElements(data.sections);
              if (replacement === false) {
                btn.disabled = true;
                btn.textContent = 'Cart updated';
                return;
              }
              if (!replacement) {
                btn.disabled = true;
                btn.textContent = 'Cart updated — refresh page';
                return;
              }
            }

            btn.textContent = 'Added!';
            setTimeout(function () {
              btn.disabled = false;
              btn.textContent = 'Add All to Cart';
            }, 2000);

            if (!showVariantPrompt(false)) {
              var offcanvas = document.getElementById('offcanvas-cart');
              if (offcanvas && window.bootstrap) {
                window.bootstrap.Offcanvas.getOrCreateInstance(offcanvas).show();
              }
            }
          })
          .catch(function () {
            btn.disabled = false;
            btn.textContent = 'Try again';
          });
      });
    },

    _addToCart: function (variantId, btn) {
      if (btn.dataset.checkCart === 'true') {
        window.location.href = (window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/') + '?cart';
        return;
      }
      btn.disabled = true;
      btn.textContent = 'Adding…';
      var formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      window.theme.addToCart(formData)
        .then(function (added) {
          if (added === false) return;
          btn.textContent = 'Added!';
          setTimeout(function () {
            btn.disabled = false;
            btn.textContent = 'Add to Cart';
          }, 1500);
        })
        .catch(function (error) {
          if (error.cartMayHaveChanged !== false) {
            btn.textContent = 'Check cart';
            btn.dataset.checkCart = 'true';
            btn.disabled = false;
            return;
          }
          btn.disabled = false;
          btn.textContent = 'Try again';
        });
    },

    _bindClearAll: function (btn, grid, emptyState, sidebar) {
      var self = this;
      var modalEl = document.getElementById('wishlist-clear-modal');
      var confirmBtn = document.getElementById('wishlist-clear-confirm');

      if (!modalEl || !confirmBtn || !window.bootstrap) return;

      var modal = window.bootstrap.Modal.getOrCreateInstance(modalEl);

      btn.addEventListener('click', function () {
        modal.show();
      });

      confirmBtn.addEventListener('click', function () {
        modal.hide();
        WishlistStore.save([]);
        WishlistStore._emit();
        grid.querySelectorAll('[data-wishlist-card]').forEach(function (card) {
          card.remove();
        });
        document.querySelectorAll('[data-wishlist-btn]').forEach(function (heartBtn) {
          self._updateBtnState(heartBtn, false);
        });
        self._showEmpty(emptyState, sidebar);
      });
    },

    _showEmpty: function (emptyState, sidebar) {
      if (emptyState) emptyState.classList.remove('d-none');
      if (sidebar) sidebar.classList.add('d-none');
      this._updatePageCount(0);
    },

    _updatePageCount: function (count) {
      if (typeof count === 'undefined') {
        count = document.querySelectorAll('[data-wishlist-card]').length;
      }
      document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
        el.textContent = count;
      });
      // Update the items label in the sidebar
      document.querySelectorAll('[data-wishlist-items-label]').forEach(function (el) {
        el.textContent = count === 1 ? 'item' : 'items';
      });
    },

    _checkEmpty: function () {
      var remaining = document.querySelectorAll('[data-wishlist-card]').length;
      if (remaining === 0) {
        var emptyState = document.querySelector('[data-wishlist-empty]');
        var sidebar = document.querySelector('[data-wishlist-sidebar]');
        this._showEmpty(emptyState, sidebar);
      }
    },

  };

  // ─── Bootstrap ──────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { WishlistUI.init(); });
  } else {
    WishlistUI.init();
  }

  // Expose for debugging
  window.WishlistStore = WishlistStore;

})();
